import { QueryClient } from '@tanstack/react-query'
import {
  PRODUCTS_TABLE_KEY,
  VARIANTS_CATALOG_KEY,
  PRODUCTS_CATALOG_KEY,
  PRODUCT_VARIANTS_KEY,
  VARIANT_SEARCH_KEY,
} from '../constants/tableQueryKeys'

/**
 * Centralized helper to invalidate all product/variant-related queries.
 * Use this after any operation that modifies products or variants (create, update, delete, bulk match).
 *
 * @param queryClient - The React Query client instance
 *
 * @example
 * ```typescript
 * const queryClient = useQueryClient()
 * await createVariant(data)
 * invalidateProductQueries(queryClient)
 * ```
 */
export const invalidateProductQueries = (queryClient: QueryClient): void => {
  // Invalidate catalog queries
  queryClient.invalidateQueries({ queryKey: [VARIANTS_CATALOG_KEY] })
  queryClient.invalidateQueries({ queryKey: [PRODUCTS_CATALOG_KEY] })
  queryClient.invalidateQueries({ queryKey: [PRODUCT_VARIANTS_KEY] })
  queryClient.invalidateQueries({ queryKey: [VARIANT_SEARCH_KEY] })

  // Invalidate legacy product queries used by ChangeProductViewModal,
  // deal creation modals, and other consumers of api.useGetProducts/useProducts
  queryClient.invalidateQueries({ queryKey: ['products'] })
  queryClient.invalidateQueries({ queryKey: ['productsOptions'] })

  // Remove product table queries (supports any tableKey variant like 'settings-products-table')
  // Use removeQueries instead of invalidateQueries to prevent showing stale data
  queryClient.removeQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      typeof query.queryKey[0] === 'string' &&
      query.queryKey[0].includes(PRODUCTS_TABLE_KEY),
  })
}
